import { RxByPrincipleSelectableReason } from '@redteclab/api/clients/bully'

import store from 'shared/store'
import buildClientAPI from 'views/providers/clientAPI'
import createA11Dialog from 'views/utils/createA11Dialog'

export const RX_BY_PRINCIPLE_MODAL_ID = 'rx-by-principle-modal'

export const RX_BY_PRINCIPLE_REASON_INPUT_ID = 'rx-by-principle-reason'

export const SELECTABLE_REASON_INPUT_NAME = 'reason'

/**
 * Modal could be opened by add to cart `form` submission,
 * by sticky buy box `link` or by redirect from cart intermediate page
 * if reason was not provided.
 */
let initiator: HTMLFormElement | HTMLAnchorElement | null = null

const getBuyBoxForm = () => {
  const form = document.querySelector<
    HTMLFormElement & {
      /**
       * Method is provided by `app/views/assets/scripts/isFormSubmitting.js`
       */
      __resetFormSubmissionAnimation?: () => void
    }
  >('[data-clientside-hook~="add2CartForm"]')

  if (!form) {
    throw new Error('No BuyBox form')
  }

  return form
}

const getStickyBuyBoxLink = () => {
  const link = document.querySelector<HTMLAnchorElement>(
    'a[name^="sticky-buy-box-"]'
  )

  if (!link) {
    throw new Error('No sticky buy box link found')
  }

  return link
}

const getRxByPrincipleReasonInput = () => {
  const input = document.getElementById(RX_BY_PRINCIPLE_REASON_INPUT_ID)

  if (input instanceof HTMLInputElement) {
    return input
  }

  throw new Error(`No input with id "${RX_BY_PRINCIPLE_REASON_INPUT_ID}" found`)
}

export const getSelectedReasonState = (
  form: HTMLFormElement
): {
  valid: boolean
  value?: RxByPrincipleSelectableReason
} => {
  let value: RxByPrincipleSelectableReason | undefined

  const reason = form.elements.namedItem(SELECTABLE_REASON_INPUT_NAME) as
    | RadioNodeList
    | HTMLInputElement

  let el: HTMLInputElement

  if (reason instanceof RadioNodeList) {
    el = reason.item(0) as HTMLInputElement

    if (reason.value) {
      value = reason.value as RxByPrincipleSelectableReason
    }
  } else {
    el = reason

    value = reason.value as RxByPrincipleSelectableReason
  }

  const valid = el.validity.valid

  return {
    valid,
    value,
  }
}

export default async () => {
  const {
    pageProperties: { product },
  } = store.getPublicRuntimeConfig()

  if (!product?.rx_by_principle?.reason_set || !product.offerId) {
    return
  }

  const { cartService } = buildClientAPI()

  const { data } = await cartService.fetchMetadata()

  if (
    data.rxByPrinciple?.offerIdsWithAlreadySelectedReasons?.includes(
      product.offerId
    )
  ) {
    return
  }

  const modalEl = document.getElementById(RX_BY_PRINCIPLE_MODAL_ID)

  if (!modalEl) {
    return
  }

  const modal = createA11Dialog({
    element: modalEl,
  })

  const buyBoxForm = getBuyBoxForm()

  const rxByPrincipleReasonInput = getRxByPrincipleReasonInput()

  const stickyBuyBoxLink = getStickyBuyBoxLink()

  modal.on('hide', () => {
    if (initiator instanceof HTMLFormElement) {
      initiator.__resetFormSubmissionAnimation?.()
    }

    initiator = null
  })

  modalEl.querySelector('form')?.addEventListener('submit', event => {
    event.preventDefault()

    const formEl = event.currentTarget as HTMLFormElement

    const { value, valid } = getSelectedReasonState(formEl)

    formEl
      .querySelector('.o-RxByPrincipleModal__error-message')
      ?.classList.toggle('o-RxByPrincipleModal__error-message--hidden', valid)

    if (!valid || !value) {
      return
    }

    if (initiator instanceof HTMLAnchorElement) {
      const { href } = initiator

      const url = new URL(href)

      url.searchParams.append(`rxByPrincipleReason[${product.offerId}]`, value)

      location.href = url.toString()
    } else if (initiator instanceof HTMLFormElement) {
      rxByPrincipleReasonInput.disabled = false
      rxByPrincipleReasonInput.value = value

      initiator.submit()
    }
  })

  buyBoxForm.addEventListener(
    'submit',
    event => {
      if (!rxByPrincipleReasonInput.value) {
        initiator = event.currentTarget as HTMLFormElement

        event.preventDefault()
        event.stopImmediatePropagation()

        modal.show()
      }
    },
    {
      capture: true, // workaround to start earlier than `formSubmissionAnimation.js` for Safari
    }
  )

  stickyBuyBoxLink.addEventListener('click', event => {
    initiator = event.currentTarget as HTMLAnchorElement

    event.preventDefault()

    modal.show()
  })

  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) {
      return
    }

    const link = event.target.closest<HTMLAnchorElement>(
      'a[name^="buy-together-"]'
    )

    if (!link) {
      return
    }

    initiator = link

    event.preventDefault()

    modal.show()
  })
}
